<template>
  <div class="input">
    <input
      v-if="tag == 'input'"
      :class="
        `input__field${(active && ' active') || ''}
        ${(size == 'small' && ' small') || ''}`
      "
      :required="required"
      @focus="isFocused"
      @blur="isNotFocused"
      v-model="val"
      :disabled="disabled"
      :name="name"
      :type="type"
      :placeholder="placeholder"
      :autofocus="autofocus"
    />
    <select
      v-else-if="tag == 'select'"
      :class="
        `input__field${(active && ' active') || ''}
        ${(size == 'small' && ' small') || ''}`
      "
      :required="required"
      @focus="isFocused"
      @blur="isNotFocused"
      @input="$emit('getValue', $event.target.value)"
      :disabled="disabled"
      :name="name"
      :placeholder="placeholder"
      :autofocus="autofocus"
    >
      <option
        v-for="(option, index) in options"
        :value="option.value"
        :selected="option.selected"
        :key="index"
      >
        {{ option.text }}
      </option>
    </select>
    <label
      v-if="label != ''"
      :class="`input__label${(active && ' active') || ''}`"
    >
      {{ label }}
    </label>
    <Icon
      v-if="tag == 'select'"
      svg="thinAngleRight"
      :class="`svg-icon svg-icon-form${(size == 'small' && ' small') || ''}`"
    />
  </div>
</template>
<script>
import { ref, watch } from "vue";
import Icon from "@/components/ui/atoms/Icon";

export default {
  components: {
    Icon
  },
  props: {
    tag: {
      type: String,
      default: () => "input",
      required: true
    },
    name: {
      type: String,
      default: () => "formField",
      required: true
    },
    type: {
      type: String,
      default: () => "text",
      required: true
    },
    placeholder: {
      type: String,
      default: () => ""
    },
    autofocus: {},
    label: {
      type: String,
      default: () => ""
    },
    required: {
      type: Boolean,
      default: () => false
    },
    disabled: {
      type: Boolean,
      default: () => false
    },
    options: {
      type: Array,
      default: () => []
    },
    size: {
      type: String,
      default: () => "",
      validator(value) {
        return ["small", "large", "normal"].includes(value);
      }
    }
  },
  emits: ["getValue"],
  setup(props, { emit }) {
    const active = ref(false);
    const val = ref("");

    const isFocused = () => {
      active.value = true;
    };
    const isNotFocused = () => {
      active.value = val.value.length;
    };

    watch(val, () => {
      emit("getValue", val.value);
    });

    return {
      isFocused,
      isNotFocused,
      active,
      val
    };
  }
};
</script>
<style lang="scss" scoped>
@use "@/assets/styles/settings/_variables.scss";
.input {
  position: relative;
  &__field {
    &:invalid {
      box-shadow: none;
    }

    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
    font-size: 1rem;
    font-weight: 400;
    text-decoration: inherit;
    text-transform: inherit;
    align-self: flex-end;
    box-sizing: border-box;
    width: 100%;
    padding: 0.75rem 1rem;
    transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1);
    border: 2px solid variables.$input-border-color;
    border-radius: variables.$br-box;
    background: none;
    appearance: none;
    outline: 0;
    &:focus {
      border-color: variables.$input-focus-border-color;
      ~ label {
        color: variables.$primary-color;
      }
      ~ .svg-icon-form {
        fill: variables.$primary-color;
      }
      ~ .svg-icon-form {
        transform: rotate(90deg);
      }
    }
    &.small {
      padding: 0.375rem 2.3rem 0.375rem 0.75rem;
      ~ label {
        transform: translate(10px, 10px) scale(1);
        &.active {
          transform: translate(10px, -10px) scale(0.95);
          font-size: 90%;
        }
      }
    }
  }
  select {
    padding-right: 2.5rem;
  }
  &__label {
    color: #9e9e9e;
    position: absolute;
    top: 0;
    left: 0;
    cursor: text;
    transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1),
      color 150ms cubic-bezier(0.4, 0, 0.2, 1);
    transform-origin: 0% 100%;
    text-align: initial;
    transform: translate(10px, 15px) scale(1);
    padding: 0.1rem 0.5rem;
    background-color: white;
    line-height: 1;
    pointer-events: none;
    &.active {
      transform: translate(10px, -10px) scale(0.95);
      font-size: 90%;
    }
  }
  .svg-icon-form {
    position: absolute;
    top: 12px;
    right: 1em;
    fill: variables.$primary-color;
    transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1),
      color 150ms cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none;
    margin: 0;
    &.small {
      top: 8px;
      right: 0.75rem;
    }
  }
}
</style>
